import React, {useMemo} from "react"
import {useLocation} from "react-router-dom";
import {NavButton} from "./NavButton";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import UploadIcon from "@mui/icons-material/Upload";
import StickyNoteIcon from "@mui/icons-material/StickyNote2"
import MessageIcon from "@mui/icons-material/Message"
import MailIcon from "@mui/icons-material/Mail"
import SMSIcon from "@mui/icons-material/Sms"
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback"
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import PhoneIcon from "@mui/icons-material/Phone";
import TuneIcon from "@mui/icons-material/Tune";
import PublicIcon from "@mui/icons-material/Public";
import PaletteIcon from "@mui/icons-material/Palette";
import EmailIcon from "@mui/icons-material/AlternateEmail";
import RouterIcon from "@mui/icons-material/Router";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import BarChartIcon from "@mui/icons-material/BarChart";

import {ClientSelectV2} from "../../lib/clients/ClientSelectV2";
import {useSiren} from "./SirenContext";
import {useChangeClient} from "./hooks/useChangeClient";
import {useAuth} from "../../context/user";
import {ToastMessage} from "./ToastMessage";
import {usePermissions} from "../../context/modulePermissions";
import {useClientModules} from "../../context/clientModules";

type LayoutProps = {
    menuCurrent: "contacts" | "groups" | "import" | "templates" | "audio" | "alert" | "hotline" | "message-contents" | "config" | "countries" | "billing-stats"
    children: React.ReactNode;
}


export function Layout({
                           children,
                           menuCurrent,
                       }: LayoutProps) {


    const {hasModule} = useClientModules()
    const {hasPermission, hasFullAccess} = usePermissions();
    const {clientID, subMenuCurrent} = useSiren();
    const {pathname} = useLocation();
    const {handleClientChange} = useChangeClient(subMenuCurrent)
    const {isSuperAdmin, isDepartmentAdmin, hasAccessLevelDepartment, hasAccessLevelClient} = useAuth()

    const enableNavigation = useMemo(() => {
        if (!isSuperAdmin) return true
        return !!clientID
    }, [isSuperAdmin, clientID])

    const showAlerts = hasPermission("siren_alert", "read")
    const showContactsSection = hasPermission("contacts", "read") || (hasAccessLevelClient && hasFullAccess("siren_config"))
    const showSirenConfigSection = hasPermission("siren_config", "read")
    const showSirenConfigFull = hasFullAccess("siren_config")
    const showHotline = hasPermission("siren_resources", "read")
    const showAudio = hasPermission("siren_resources", "read")
    const showAudioFull = hasFullAccess("siren_resources")


    if (!hasModule("siren")) {
        return (
            <div className="pt-36 px-7 flex flex-col gap-4 pb-8">
                <p className="text-center">
                    The Siren module is not enabled for your organisation.
                </p>
            </div>
        )
    }
    if (!hasPermission("siren_config", "read") && !hasPermission("siren_resources", "read") && !hasPermission("siren_alert", "read") && !hasPermission("contacts", "read")) {
        return (
            <div className="pt-36 px-7 flex flex-col gap-4 pb-8">
                <p className="text-center">
                    You don&#39;t have permission to access siren.
                </p>
            </div>
        )
    }


    return (
        <div className="pt-36 px-7 flex flex-col gap-4 pb-8">
            <div className="flex flex-col gap-4">
                <ToastMessage/>

                <div className="bg-white shadow-md p-4 pr-16 flex flex-row flex-wrap gap-3 items-center">
                    <NavButton text={"Alerts"}
                               to={"/siren/alerts?subPage=log"}
                               icon={<NotificationsIcon/>}
                               active={menuCurrent == "alert"}
                               disabled={!enableNavigation}
                               show={hasPermission("siren_alert", "read")}
                    />

                    {showAlerts && showContactsSection && <div className="w-px h-6 bg-light-grey"/>}

                    <NavButton text={"Contacts"}
                               to={"/siren/contacts?subPage=active"}
                               icon={<PersonIcon/>}
                               active={menuCurrent == "contacts"}
                               disabled={!enableNavigation}
                               show={hasPermission("contacts", "read")}
                    />
                    <NavButton text={"Groups"}
                               to={"/siren/groups?subPage=active"}
                               icon={<GroupIcon/>}
                               active={menuCurrent == "groups"}
                               disabled={!enableNavigation}
                               show={hasPermission("contacts", "read")}
                    />
                    {hasAccessLevelClient && (
                        <NavButton text={"Import"}
                                   to={"/siren/import?subPage=new_import"}
                                   icon={<UploadIcon/>}
                                   active={menuCurrent == "import"}
                                   disabled={!enableNavigation}
                                   show={hasFullAccess("siren_config")}
                        />
                    )}

                    {showContactsSection && showAudio && <div className="w-px h-6 bg-light-grey"/>}

                    <NavButton text={"Templates"}
                               to={"/siren/templates?subPage=alert"}
                               icon={<StickyNoteIcon/>}
                               active={menuCurrent == "templates"}
                               disabled={!enableNavigation}
                               show={hasPermission("siren_resources", "read")}
                    />

                    <NavButton text={"Audio"}
                               to={"/siren/audio?subPage=files"}
                               icon={<VolumeUpIcon/>}
                               active={menuCurrent == "audio"}
                               disabled={!enableNavigation}
                               show={showAudio}
                    />

                    {showAudio && showHotline && <div className="w-px h-6 bg-light-grey"/>}

                    <NavButton text={"Hotline"}
                               to={"/siren/hotlines?subPage=hotlines"}
                               icon={<PhoneIcon/>}
                               active={menuCurrent == "hotline"}
                               disabled={!enableNavigation}
                               show={showHotline}
                    />


                    {showSirenConfigSection && <>
                        {(showAlerts || showContactsSection || showSirenConfigFull || showHotline) &&
                            <div className="w-px h-6 bg-light-grey"/>}
                        <NavButton text={"Config"}
                                   to={"/siren/recipient-portal"}
                                   icon={<TuneIcon/>}
                                   active={menuCurrent == "config"}
                                   show={true}
                        />
                        <NavButton text={"Countries"}
                                   to={"/siren/countries"}
                                   icon={<PublicIcon/>}
                                   active={menuCurrent == "countries"}
                                   show={true}
                        />
                        {isSuperAdmin && (
                            <>
                                <div className="w-px h-6 bg-light-grey"/>
                                <NavButton text={"Billing Stats"}
                                           to={"/siren/billing-stats"}
                                           icon={<BarChartIcon/>}
                                           active={menuCurrent == "billing-stats"}
                                           show={true}
                                />
                                <div className="w-px h-6 bg-light-grey"/>

                                <div className="w-[300px]">
                                    <ClientSelectV2 initialID={clientID} key={clientID} module="siren"
                                                    onChange={c => handleClientChange(c?.id)}/>
                                </div>
                            </>
                        )}

                    </>}
                </div>

                {menuCurrent === "hotline" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"Hotlines"}
                                   to={"/siren/hotlines?subPage=hotlines"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "hotlines"}
                                   disabled={!enableNavigation}
                                   show={showHotline}

                        />

                        <NavButton text={"Numbers"}
                                   to={"/siren/hotlines?subPage=numbers"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "numbers"}
                                   disabled={!enableNavigation}
                                   show={showHotline && isSuperAdmin}
                        />

                        <NavButton text={"Create Hotline"}
                                   to={"/siren/hotlines?subPage=create_hotline"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "create_hotline"}
                                   disabled={!enableNavigation}
                                   show={hasFullAccess("siren_resources") && isSuperAdmin}

                        />

                        <NavButton text={"Create Number"}
                                   to={"/siren/hotlines?subPage=create_number"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "create_number"}
                                   disabled={!enableNavigation}
                                   show={hasFullAccess("siren_resources") && isSuperAdmin}
                        />


                    </div>
                )}

                {menuCurrent === "contacts" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"Active"}
                                   to={"/siren/contacts?subPage=active"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "active"}
                                   disabled={!enableNavigation}
                                   show={hasPermission("contacts", "read")}
                        />

                        <NavButton text={"Archived"}
                                   to={"/siren/contacts?subPage=archived"}
                                   icon={<DeleteIcon/>}
                                   active={subMenuCurrent === "archived"}
                                   disabled={!enableNavigation}
                                   show={hasPermission("contacts", "read")}
                        />

                        {hasAccessLevelClient && (
                            <NavButton text={"Create Contact"}
                                       to={"/siren/contacts?subPage=create"}
                                       icon={<CreateIcon/>}
                                       active={subMenuCurrent === "create"}
                                       disabled={!enableNavigation}
                                       show={hasFullAccess("contacts")}
                            />
                        )}

                        <NavButton text={"SMS Opt-ins"}
                                   to={"/siren/contacts?subPage=opt-ins"}
                                   icon={<PhoneCallbackIcon/>}
                                   active={subMenuCurrent === "opt-ins"}
                                   disabled={!enableNavigation}
                                   show={hasPermission("contacts", "read")}
                        />
                    </div>
                )}

                {menuCurrent === "groups" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"Active"}
                                   to={"/siren/groups?subPage=active"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "active"}
                                   disabled={!enableNavigation}
                                   show={hasPermission("contacts", "read")}
                        />

                        <NavButton text={"Archived"}
                                   to={"/siren/groups?subPage=archived"}
                                   icon={<DeleteIcon/>}
                                   active={subMenuCurrent === "archived"}
                                   disabled={!enableNavigation}
                                   show={hasPermission("contacts", "read")}
                        />

                        {hasAccessLevelClient && (
                            <NavButton text={"Create Group"}
                                       to={"/siren/groups?subPage=create"}
                                       icon={<CreateIcon/>}
                                       active={subMenuCurrent === "create"}
                                       disabled={!enableNavigation}
                                       show={hasFullAccess("contacts")}
                            />
                        )}
                    </div>
                )}


                {menuCurrent === "import" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"New Import"}
                                   to={"/siren/import?subPage=new_import"}
                                   icon={<UploadIcon/>}
                                   active={subMenuCurrent === "new_import"}
                                   disabled={!enableNavigation}
                                   show={hasFullAccess("contacts")}
                        />

                        <NavButton text={"History"}
                                   to={"/siren/import?subPage=import_history"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "import_history"}
                                   disabled={!enableNavigation}
                                   show={hasFullAccess("contacts")}

                        />


                    </div>
                )}

                {menuCurrent === "templates" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"Alert"}
                                   to={"/siren/templates?subPage=alert"}
                                   icon={<MailIcon/>}
                                   active={subMenuCurrent === "alert"}
                                   disabled={!enableNavigation}
                                   show={hasPermission("siren_resources", "read")}

                        />

                        <NavButton text={"Message"}
                                   to={"/siren/templates?subPage=message"}
                                   icon={<SMSIcon/>}
                                   active={subMenuCurrent === "message"}
                                   disabled={!enableNavigation}
                                   show={hasPermission("siren_resources", "read")}
                        />


                        <NavButton text={"Create Template"}
                                   to={"/siren/templates?subPage=create"}
                                   icon={<CreateIcon/>}
                                   active={subMenuCurrent === "create"}
                                   disabled={!enableNavigation}
                                   show={hasFullAccess("siren_resources")}
                        />

                    </div>
                )}

                {menuCurrent === "message-contents" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"List"}
                                   to={"/siren/message-contents?subPage=list"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "list"}
                                   disabled={!enableNavigation}
                                   show={hasFullAccess("siren_config")}
                        />
                        {hasAccessLevelDepartment && (
                            <NavButton text={"Create"}
                                       to={"/siren/message-contents?subPage=create"}
                                       icon={<CreateIcon/>}
                                       active={subMenuCurrent === "create"}
                                       disabled={!enableNavigation}
                                       show={hasFullAccess("siren_config")}
                            />
                        )}
                    </div>
                )}

                {menuCurrent === "audio" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"Files"}
                                   to={"/siren/audio?subPage=files"}
                                   icon={<LibraryMusicIcon/>}
                                   active={subMenuCurrent === "files"}
                                   disabled={!enableNavigation}
                                   show={showAudio}
                        />

                        <NavButton text={"Upload"}
                                   to={"/siren/audio?subPage=upload"}
                                   icon={<UploadIcon/>}
                                   active={subMenuCurrent === "upload"}
                                   disabled={!enableNavigation}
                                   show={showAudioFull}
                        />

                        <NavButton text={"Text-To-Speech"}
                                   to={"/siren/audio?subPage=text-to-speech"}
                                   icon={<SpeakerNotesIcon/>}
                                   active={subMenuCurrent === "text-to-speech"}
                                   disabled={!enableNavigation}
                                   show={showAudioFull}
                        />

                    </div>

                )}

                {menuCurrent === "alert" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"History"}
                                   to={"/siren/alerts?subPage=log"}
                                   icon={<FormatListBulletedIcon/>}
                                   active={subMenuCurrent === "log"}
                                   disabled={!enableNavigation}
                                   show={hasPermission("siren_alert", "read")}
                        />

                        <NavButton text={"Create"}
                                   to={"/siren/alerts?subPage=create"}
                                   icon={<CreateIcon/>}
                                   active={subMenuCurrent === "create"}
                                   disabled={!enableNavigation}
                                   show={hasFullAccess("siren_alert")}
                        />


                    </div>
                )}

                {menuCurrent === "config" && (
                    <div className="bg-white shadow-md p-4 flex flex-row gap-4 items-center">
                        <NavButton text={"Portal"}
                                   to={"/siren/recipient-portal"}
                                   icon={<PaletteIcon/>}
                                   active={pathname.startsWith("/siren/recipient-portal")}
                                   show={!isDepartmentAdmin}
                        />
                        <NavButton text={"SMS"}
                                   to={"/siren/sms-config"}
                                   icon={<SMSIcon/>}
                                   active={pathname.startsWith("/siren/sms-config")}
                                   show={true}
                        />

                        <NavButton text={"Phone Calls"}
                                   to={"/siren/phone-call-config"}
                                   icon={<PhoneIcon/>}
                                   active={pathname.startsWith("/siren/phone-call-config")}
                                   show={true}
                        />

                        <NavButton text={"Email"}
                                   to={"/siren/email-branding"}
                                   icon={<EmailIcon/>}
                                   active={pathname.startsWith("/siren/email-branding")}
                                   show={true}
                        />
                        <NavButton text={"Broadcast Config"}
                                   to={"/siren/default-broadcast-config"}
                                   icon={<RouterIcon/>}
                                   active={pathname.startsWith("/siren/default-broadcast-config")}
                                   show={true}
                        />
                        <NavButton text={"Delivery Methods"}
                                   to={"/siren/default-delivery-methods"}
                                   icon={<NotificationsIcon/>}
                                   active={pathname.startsWith("/siren/default-delivery-methods")}
                                   show={true}
                        />

                        <NavButton text={"Drivers"}
                                   to={"/siren/drivers"}
                                   icon={<RouterIcon/>}
                                   active={pathname.startsWith("/siren/drivers") && !pathname.startsWith("/siren/drivers/client-config")}
                                   show={isSuperAdmin}
                        />
                        <NavButton text={"Driver Access"}
                                   to={"/siren/drivers/client-config"}
                                   icon={<VpnKeyIcon/>}
                                   active={pathname.startsWith("/siren/drivers/client-config")}
                                   show={isSuperAdmin}
                        />
                    </div>
                )}


            </div>
            {children}
        </div>
    )
}
